/**
 * Sudoku rules, generation, scoring, and highlighting. No DOM, no Node — the web
 * board and a later Expo grid have to play by the same checks.
 *
 * Behaviour matches the legacy sudoku/script.js:
 *   easy    45 clues, 5 hints
 *   medium  35 clues, 4 hints
 *   hard    27 clues, 3 hints
 *   expert  22 clues, 2 hints
 *   +10 first correct fill, -5 mistake (floor 0), -20 hint (floor 0)
 *   on win: +max(0, 1000 - seconds) - mistakes*10 - hints*20
 */

export type SudokuDigit = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type SudokuCell = 0 | SudokuDigit;
export type SudokuDifficulty = "easy" | "medium" | "hard" | "expert";
export type SudokuSameNumberKind = "fixed" | "editable";
export type Rng = () => number;

export const SUDOKU_SIZE = 81;
export const SUDOKU_DIGITS: readonly SudokuDigit[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const SUDOKU_DIFFICULTIES: readonly SudokuDifficulty[] = [
  "easy",
  "medium",
  "hard",
  "expert",
];

export const SUDOKU_DIFFICULTY_LABELS: Record<SudokuDifficulty, string> = {
  easy: "Łatwy",
  medium: "Średni",
  hard: "Trudny",
  expert: "Ekspert",
};

export const SUDOKU_DIFFICULTY_SETTINGS: Record<
  SudokuDifficulty,
  { clues: number; hints: number }
> = {
  easy: { clues: 45, hints: 5 },
  medium: { clues: 35, hints: 4 },
  hard: { clues: 27, hints: 3 },
  expert: { clues: 22, hints: 2 },
};

export interface SudokuState {
  /** Starting clues only (hints are not recorded here). */
  puzzle: SudokuCell[];
  board: SudokuCell[];
  solution: SudokuCell[];
  /** Bitmask per cell; bit `d` means pencil-mark d is on. */
  notes: number[];
  /** Original clues plus cells filled by a hint. */
  given: boolean[];
  selectedCell: number | null;
  selectedNumber: SudokuCell;
  notesMode: boolean;
  difficulty: SudokuDifficulty;
  score: number;
  mistakes: number;
  hintsRemaining: number;
  hintsUsed: number;
  gameActive: boolean;
}

export interface SudokuCellView {
  index: number;
  value: SudokuCell;
  given: boolean;
  notes: SudokuDigit[];
  selected: boolean;
  region: boolean;
  sameNumber: SudokuSameNumberKind | null;
  wrong: boolean;
}

export interface SudokuScore {
  id: number;
  username: string;
  difficulty: SudokuDifficulty;
  time: number;
  score: number;
  hintsUsed: number;
  mistakes: number;
  createdAt: string;
}

export type PlaceDigitResult =
  | { ok: false }
  | { ok: true; state: SudokuState; flash: "correct" | "error" | null; won: boolean };

export type HintResult =
  | { ok: false; reason: "inactive" | "none" | "full" }
  | { ok: true; state: SudokuState; index: number; won: boolean };

export interface SudokuValidateResult {
  hasErrors: boolean;
  correctCount: number;
  errorIndexes: number[];
  correctIndexes: number[];
}

export function isSudokuDifficulty(value: unknown): value is SudokuDifficulty {
  return (
    value === "easy" || value === "medium" || value === "hard" || value === "expert"
  );
}

export function isSudokuDigit(value: number): value is SudokuDigit {
  return Number.isInteger(value) && value >= 1 && value <= 9;
}

export function parseSudokuCells(value: unknown): SudokuCell[] | null {
  if (!Array.isArray(value) || value.length !== SUDOKU_SIZE) return null;
  const out: SudokuCell[] = [];
  for (const item of value) {
    if (typeof item !== "number" || !Number.isInteger(item) || item < 0 || item > 9) {
      return null;
    }
    out.push(item as SudokuCell);
  }
  return out;
}

export function formatSudokuTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

export function noteHas(mask: number, digit: SudokuDigit): boolean {
  return (mask & (1 << digit)) !== 0;
}

export function toggleNoteBit(mask: number, digit: SudokuDigit): number {
  return mask ^ (1 << digit);
}

export function notesToDigits(mask: number): SudokuDigit[] {
  const out: SudokuDigit[] = [];
  for (const digit of SUDOKU_DIGITS) {
    if (noteHas(mask, digit)) out.push(digit);
  }
  return out;
}

export function isInSameRegion(index1: number, index2: number): boolean {
  const row1 = Math.floor(index1 / 9);
  const col1 = index1 % 9;
  const row2 = Math.floor(index2 / 9);
  const col2 = index2 % 9;
  if (row1 === row2 || col1 === col2) return true;
  const box1 = Math.floor(row1 / 3) * 3 + Math.floor(col1 / 3);
  const box2 = Math.floor(row2 / 3) * 3 + Math.floor(col2 / 3);
  return box1 === box2;
}

export function canPlace(
  board: readonly SudokuCell[],
  index: number,
  num: SudokuDigit,
): boolean {
  const row = Math.floor(index / 9);
  const col = index % 9;
  for (let x = 0; x < 9; x++) {
    if (board[row * 9 + x] === num) return false;
    if (board[x * 9 + col] === num) return false;
  }
  const startRow = Math.floor(row / 3) * 3;
  const startCol = Math.floor(col / 3) * 3;
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      if (board[(startRow + i) * 9 + (startCol + j)] === num) return false;
    }
  }
  return true;
}

export function isCompleteValidSudoku(board: readonly SudokuCell[]): boolean {
  if (board.length !== SUDOKU_SIZE) return false;
  for (const cell of board) {
    if (cell < 1 || cell > 9) return false;
  }
  for (let unit = 0; unit < 9; unit++) {
    const row = new Set<number>();
    const col = new Set<number>();
    const box = new Set<number>();
    for (let k = 0; k < 9; k++) {
      row.add(board[unit * 9 + k] ?? 0);
      col.add(board[k * 9 + unit] ?? 0);
      const boxRow = Math.floor(unit / 3) * 3 + Math.floor(k / 3);
      const boxCol = (unit % 3) * 3 + (k % 3);
      box.add(board[boxRow * 9 + boxCol] ?? 0);
    }
    if (row.size !== 9 || col.size !== 9 || box.size !== 9) return false;
  }
  return true;
}

export function isPuzzleOfSolution(
  puzzle: readonly SudokuCell[],
  solution: readonly SudokuCell[],
): boolean {
  if (puzzle.length !== SUDOKU_SIZE || solution.length !== SUDOKU_SIZE) return false;
  for (let i = 0; i < SUDOKU_SIZE; i++) {
    const given = puzzle[i] ?? 0;
    const solved = solution[i] ?? 0;
    if (given !== 0 && given !== solved) return false;
  }
  return true;
}

export function clueCount(puzzle: readonly SudokuCell[]): number {
  let count = 0;
  for (const cell of puzzle) {
    if (cell !== 0) count += 1;
  }
  return count;
}

export function countSolutions(board: readonly SudokuCell[], limit = 2): number {
  const work = board.slice();
  let count = 0;

  const solve = (pos: number): void => {
    if (count >= limit) return;
    if (pos === SUDOKU_SIZE) {
      count += 1;
      return;
    }
    if (work[pos] !== 0) {
      solve(pos + 1);
      return;
    }
    for (const num of SUDOKU_DIGITS) {
      if (canPlace(work, pos, num)) {
        work[pos] = num;
        solve(pos + 1);
        work[pos] = 0;
        if (count >= limit) return;
      }
    }
  };

  solve(0);
  return count;
}

export function boardsEqual(
  left: readonly SudokuCell[],
  right: readonly SudokuCell[],
): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

export function isWon(board: readonly SudokuCell[], solution: readonly SudokuCell[]): boolean {
  return boardsEqual(board, solution) && isCompleteValidSudoku(board);
}

export function maxSudokuScore(puzzle: readonly SudokuCell[]): number {
  const empty = SUDOKU_SIZE - clueCount(puzzle);
  return empty * 10 + 1000;
}

/**
 * Rejects a submitted win that the server cannot independently confirm. Generation
 * stays client-side like the original; ranking still checks uniqueness and bounds.
 */
export function sudokuScoreRejection(input: {
  difficulty: SudokuDifficulty;
  puzzle: readonly SudokuCell[];
  solution: readonly SudokuCell[];
  board: readonly SudokuCell[];
  time: number;
  score: number;
  hintsUsed: number;
  mistakes: number;
}): string | null {
  const settings = SUDOKU_DIFFICULTY_SETTINGS[input.difficulty];
  if (!isCompleteValidSudoku(input.solution)) {
    return "Rozwiązanie nie jest poprawnym sudoku";
  }
  if (!isPuzzleOfSolution(input.puzzle, input.solution)) {
    return "Plansza nie pasuje do rozwiązania";
  }
  if (clueCount(input.puzzle) < settings.clues) {
    return "Za mało podanych cyfr jak na ten poziom";
  }
  if (countSolutions(input.puzzle, 2) !== 1) {
    return "Plansza nie ma jednoznacznego rozwiązania";
  }
  if (!boardsEqual(input.board, input.solution)) {
    return "Plansza nie jest ukończona";
  }
  if (input.hintsUsed < 0 || input.hintsUsed > settings.hints) {
    return "Nieprawidłowa liczba podpowiedzi";
  }
  if (input.mistakes < 0 || input.time < 0) {
    return "Nieprawidłowe statystyki";
  }
  if (input.score < 0 || input.score > maxSudokuScore(input.puzzle)) {
    return "Nieprawidłowy wynik";
  }
  return null;
}

export function newSudokuGame(
  difficulty: SudokuDifficulty,
  rng: Rng = Math.random,
): SudokuState {
  const generated = generatePuzzle(difficulty, rng);
  const given = generated.board.map((value) => value !== 0);
  return {
    puzzle: generated.board.slice(),
    board: generated.board,
    solution: generated.solution,
    notes: Array.from({ length: SUDOKU_SIZE }, () => 0),
    given,
    selectedCell: null,
    selectedNumber: 0,
    notesMode: false,
    difficulty,
    score: 0,
    mistakes: 0,
    hintsRemaining: SUDOKU_DIFFICULTY_SETTINGS[difficulty].hints,
    hintsUsed: 0,
    gameActive: true,
  };
}

export function generatePuzzle(
  difficulty: SudokuDifficulty,
  rng: Rng = Math.random,
): { board: SudokuCell[]; solution: SudokuCell[] } {
  const solution = generateSolvedBoard(rng);
  const board = solution.slice();
  const cellsToRemove = SUDOKU_SIZE - SUDOKU_DIFFICULTY_SETTINGS[difficulty].clues;
  const positions = shuffle(
    Array.from({ length: SUDOKU_SIZE }, (_, index) => index),
    rng,
  );

  let removed = 0;
  for (const pos of positions) {
    if (removed >= cellsToRemove) break;
    const backup = board[pos] ?? 0;
    board[pos] = 0;
    if (countSolutions(board, 2) === 1) {
      removed += 1;
    } else {
      board[pos] = backup as SudokuCell;
    }
  }

  return { board, solution };
}

export function selectCell(state: SudokuState, index: number): SudokuState {
  if (!state.gameActive || index < 0 || index >= SUDOKU_SIZE) return state;
  const next = copyState(state);
  next.selectedCell = index;
  next.selectedNumber = next.board[index] ?? 0;
  return next;
}

export function toggleNotesMode(state: SudokuState): SudokuState {
  if (!state.gameActive) return state;
  const next = copyState(state);
  next.notesMode = !next.notesMode;
  return next;
}

export function placeDigit(state: SudokuState, num: SudokuDigit): PlaceDigitResult {
  if (!state.gameActive || state.selectedCell === null) return { ok: false };
  const cellIndex = state.selectedCell;
  if (state.given[cellIndex]) return { ok: false };

  const next = copyState(state);

  if (next.notesMode) {
    const mask = next.notes[cellIndex] ?? 0;
    next.notes[cellIndex] = toggleNoteBit(mask, num);
    return { ok: true, state: next, flash: null, won: false };
  }

  const previousValue = next.board[cellIndex] ?? 0;
  next.board[cellIndex] = num;
  next.notes[cellIndex] = 0;

  let flash: "correct" | "error" | null = null;
  const solutionValue = next.solution[cellIndex] ?? 0;
  if (num === solutionValue) {
    flash = "correct";
    if (previousValue === 0) next.score += 10;
  } else {
    flash = "error";
    next.mistakes += 1;
    next.score = Math.max(0, next.score - 5);
  }

  next.selectedNumber = num;
  const won = isWon(next.board, next.solution);
  return { ok: true, state: next, flash, won };
}

export function clearSelectedCell(state: SudokuState): SudokuState {
  if (!state.gameActive || state.selectedCell === null) return state;
  if (state.given[state.selectedCell]) return state;
  const next = copyState(state);
  next.board[state.selectedCell] = 0;
  next.notes[state.selectedCell] = 0;
  next.selectedNumber = 0;
  return next;
}

export function giveHint(state: SudokuState, rng: Rng = Math.random): HintResult {
  if (!state.gameActive) return { ok: false, reason: "inactive" };
  if (state.hintsRemaining <= 0) return { ok: false, reason: "none" };

  const empty: number[] = [];
  for (let i = 0; i < SUDOKU_SIZE; i++) {
    if ((state.board[i] ?? 0) === 0 && !state.given[i]) empty.push(i);
  }
  if (empty.length === 0) return { ok: false, reason: "full" };

  const pick = empty[Math.floor(rng() * empty.length)];
  if (pick === undefined) return { ok: false, reason: "full" };

  const next = copyState(state);
  next.board[pick] = next.solution[pick] ?? 0;
  next.notes[pick] = 0;
  next.given[pick] = true;
  next.hintsRemaining -= 1;
  next.hintsUsed += 1;
  next.score = Math.max(0, next.score - 20);
  const won = isWon(next.board, next.solution);
  return { ok: true, state: next, index: pick, won };
}

export function validateFilled(state: SudokuState): SudokuValidateResult {
  const errorIndexes: number[] = [];
  const correctIndexes: number[] = [];
  for (let i = 0; i < SUDOKU_SIZE; i++) {
    const value = state.board[i] ?? 0;
    if (value === 0 || state.given[i]) continue;
    if (value !== (state.solution[i] ?? 0)) errorIndexes.push(i);
    else correctIndexes.push(i);
  }
  return {
    hasErrors: errorIndexes.length > 0,
    correctCount: correctIndexes.length,
    errorIndexes,
    correctIndexes,
  };
}

export function navigateCell(
  state: SudokuState,
  key: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight",
): SudokuState {
  if (!state.gameActive) return state;
  const next = copyState(state);
  if (next.selectedCell === null) {
    next.selectedCell = 0;
  } else {
    const row = Math.floor(next.selectedCell / 9);
    const col = next.selectedCell % 9;
    if (key === "ArrowUp" && row > 0) next.selectedCell -= 9;
    if (key === "ArrowDown" && row < 8) next.selectedCell += 9;
    if (key === "ArrowLeft" && col > 0) next.selectedCell -= 1;
    if (key === "ArrowRight" && col < 8) next.selectedCell += 1;
  }
  next.selectedNumber = next.board[next.selectedCell] ?? 0;
  return next;
}

export function finalizeWin(state: SudokuState, timeElapsed: number): SudokuState {
  const timeBonus = Math.max(0, 1000 - timeElapsed);
  const mistakePenalty = state.mistakes * 10;
  const hintPenalty = state.hintsUsed * 20;
  const next = copyState(state);
  next.gameActive = false;
  next.score = Math.max(0, next.score + timeBonus - mistakePenalty - hintPenalty);
  return next;
}

export function sudokuCellView(state: SudokuState, index: number): SudokuCellView {
  const value = state.board[index] ?? 0;
  const given = Boolean(state.given[index]);
  const selected = state.selectedCell === index;
  const region =
    state.selectedCell !== null &&
    index !== state.selectedCell &&
    isInSameRegion(index, state.selectedCell);
  let sameNumber: SudokuSameNumberKind | null = null;
  if (state.selectedNumber !== 0 && value === state.selectedNumber) {
    sameNumber = given ? "fixed" : "editable";
  }
  return {
    index,
    value,
    given,
    notes: value === 0 && !given ? notesToDigits(state.notes[index] ?? 0) : [],
    selected,
    region,
    sameNumber,
    wrong: !given && value !== 0 && value !== (state.solution[index] ?? 0),
  };
}

/** Fill every empty cell except one, so a real last digit can still win the game. */
export function fillAllButOne(state: SudokuState): SudokuState {
  const next = copyState(state);
  let last = -1;
  for (let i = SUDOKU_SIZE - 1; i >= 0; i--) {
    if (!next.given[i]) {
      last = i;
      break;
    }
  }
  next.board = next.solution.slice();
  if (last >= 0) {
    next.board[last] = 0;
    next.notes[last] = 0;
    next.selectedCell = last;
    next.selectedNumber = 0;
  }
  return next;
}

function generateSolvedBoard(rng: Rng): SudokuCell[] {
  const board = emptyBoard();
  if (!solveSudoku(board, 0, rng)) {
    throw new Error("sudoku: failed to generate a solved board");
  }
  return board;
}

function solveSudoku(board: SudokuCell[], pos: number, rng: Rng): boolean {
  if (pos === SUDOKU_SIZE) return true;
  if (board[pos] !== 0) return solveSudoku(board, pos + 1, rng);

  for (const num of shuffle(SUDOKU_DIGITS, rng)) {
    if (canPlace(board, pos, num)) {
      board[pos] = num;
      if (solveSudoku(board, pos + 1, rng)) return true;
      board[pos] = 0;
    }
  }
  return false;
}

function emptyBoard(): SudokuCell[] {
  return Array.from({ length: SUDOKU_SIZE }, () => 0);
}

function copyState(state: SudokuState): SudokuState {
  return {
    ...state,
    puzzle: state.puzzle.slice(),
    board: state.board.slice(),
    solution: state.solution.slice(),
    notes: state.notes.slice(),
    given: state.given.slice(),
  };
}

function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const array = items.slice();
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const current = array[i];
    const swap = array[j];
    if (current === undefined || swap === undefined) continue;
    array[i] = swap;
    array[j] = current;
  }
  return array;
}
